import { Box, Button, ButtonGroup, Chip, Container, Grid, Typography } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { useContext, useEffect, useMemo, useState } from "react"
import { StyledHeaderContainer, StyledTableContainer } from "shared/styled/Global"
import { ActionType } from "shared/vehicle/components/InspectionBookingSidebar.interfaces"
import { initialModalState, ModalDialog } from "shared/vehicle/components/ModalDialog"
import CustomFieldGroupForm from "./customFieldGroupForm"
import { t } from "i18next"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"
import ChipTooltip from "components/ChipTooltip/ChipTooltip"

const tableDesc = `Here you choose which <strong>own fields</strong> should be active for each <strong>vehicle group</strong> or <strong>individual vehicle</strong>. <br /> In this way, only relevant fields are displayed for the vehicles to which they apply, which makes it easier to keep the information clear and adapted to different types of vehicles or areas of use.`

const CustomFieldGroups = () => {
  const appContext = useContext(AppContext)
  const loadingContext = useContext(GlobalLoaderContext)
  const [listResult, listApi] = useApi()
  const [addResult, addApi] = useApi()
  const [updateResult, updateApi] = useApi()
  const [editItemResult, editItemApi] = useApi()
  const [deleteGroupResult, deleteGroupApi] = useApi()
  const [modalData, setModalData] = useState(initialModalState)
  const [clickedGroup, setClickedGroup] = useState()
  const [editMode, setEditMode] = useState(false)
  const [showSidebarForm, setShowSidebarForm] = useState(false)

  useEffect(() => {
    if (updateResult.status === 2 || addResult.status === 2) {
      listApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
      appContext.toggleSideBar()
    }
  }, [updateResult, addResult])

  useEffect(() => {
    if (deleteGroupResult.status === 2) {
      listApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
    }
  }, [deleteGroupResult])

  useEffect(() => {
    loadingContext.setLoading(() => listResult.status === 1)
  })

  useEffect(() => {
    listApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
  }, [listApi])

  const openEdit = (id) => {
    setEditMode(true)
    editItemApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroupbyid.replace("{id}", id))
    updateApi.reset()
    addApi.reset()
    deleteGroupApi.reset()
  }

  const openAdd = () => {
    setEditMode(false)
    editItemApi.reset()
    updateApi.reset()
    addApi.reset()
    deleteGroupApi.reset()
  }

  const onAddSubmit = (values) => {
    addApi.put(apiEndpoints.vehicleregistryadmin.upsertcustomfieldgroup, values)
  }

  const onUpdateSubmit = (values) => {
    updateApi.put(apiEndpoints.vehicleregistryadmin.upsertcustomfieldgroup, values)
  }

  const customFieldGroupColumns = useMemo(() => {
    const columns = [
      {
        accessorKey: "name",
        header: t("global.name", "Name")
      },
      {
        accessorFn: (row) => {
          return row?.customFieldNames
            ? row.customFieldNames.map((customField) => customField.name).join(", ")
            : ""
        },
        header: t("global.field", "Field")
      },
      {
        accessorKey: "vehiclesInfo",
        cell: ({ row }) => {
          const vehiclesInfo = row.original.vehiclesInfo
          const visible = vehiclesInfo.slice(0, 3)
          const remaining = vehiclesInfo.slice(3)
          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {visible.map((vehicle) => (
                <Chip key={vehicle.id} label={vehicle.regNumber} size="small" variant="outlined" />
              ))}

              {remaining.length > 0 && (
                <ChipTooltip
                  label={`+${remaining.length} till`}
                  remaining={remaining.map((v) => v.regNumber).join(", ")}
                />
              )}
            </Box>
          )
        },
        header: t("global.vehicle", "Vehicle")
      }
    ]

    if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
      columns.push({
        accessorKey: "id",
        align: "right",
        header: "",
        meta: {
          hiddenFilter: true
        },
        cell: ({ cell }) => {
          return (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <TableButton
                type={TableButtonTypeEnum.Edit}
                handleOnClick={() => {
                  appContext.toggleSideBar()
                  openEdit(cell.row.original.id)
                }}
              />
              <TableButton
                type={TableButtonTypeEnum.Delete}
                handleOnClick={() => {
                  const groupName = cell.row.original.name
                  setClickedGroup(cell.row.original)
                  setModalData({
                    actionType: ActionType.Confirm,
                    isOpen: true,
                    title: t("vehicleregistry.delete_group", "Delete group"),
                    message: `${t(
                      "global.are_you_sure_you_want_to_delete",
                      "Are you sure you want to delete?"
                    )} ${groupName}?`
                  })
                }}
              />
            </div>
          )
        }
      })
    }
    return columns
  }, [])

  return (
    <>
      <ModalDialog
        modalData={modalData}
        setModalData={setModalData}
        setShowSidebar={setShowSidebarForm}
        handleSubmit={() => {
          const groupId = clickedGroup?.id
          if (!groupId) return
          const url = apiEndpoints.vehicleregistryadmin.deletecustomfieldgroup.replace(
            "{customFieldGroupId}",
            groupId
          )
          deleteGroupApi.del(url)
        }}
      />
      <CustomFieldGroupForm
        editItemResult={editItemResult}
        addResult={addResult}
        updateResult={updateResult}
        editMode={editMode}
        onAddSubmit={onAddSubmit}
        onUpdateSubmit={onUpdateSubmit}
      />
      <StyledHeaderContainer>
        <Box>
          <Typography variant="h1">
            {t("custom_fields.couple_custom_fields", "Couple custom fields")}
          </Typography>
        </Box>
        <ButtonGroup color="primary">
          <Button
            color="primary"
            onClick={() => {
              openAdd()
              appContext.toggleSideBar()
            }}
            variant="contained"
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            {t("global.add_coupling", "Add coupling")}
          </Button>
        </ButtonGroup>
      </StyledHeaderContainer>
      <StyledTableContainer>
        <Box display="flex" alignItems="center" gap={1} px={{ xs: 2, sm: 3 }} pt={3}>
          <Typography variant="labelSmall">{t("global.information", "Information")}</Typography>
          <DynamicTooltip t={t} title={t("custom_fields.couple_custom_fields", "Couple custom fields")}>
            <Grid item>
              <Typography fontWeight="600" variant="labelSmall">
                {t("vehicleregistry.how_to_connect_custom_fields", "This is how you connect your own fields")}
              </Typography>
              <Typography
                sx={{ mt: 0.5 }}
                variant="bodySmall"
                color="textSecondary"
                dangerouslySetInnerHTML={{
                  __html: t("custom_fields.group_table_desc", tableDesc)
                }}
              />
            </Grid>
          </DynamicTooltip>
        </Box>
        <ClpTable
          columns={customFieldGroupColumns}
          cellProps={(row) => ({
            onClick: () => {
              openEdit(row.original.id)
            }
          })}
          rows={listResult.status === 2 && listResult.data}
          status={listResult.status}
          loadingState={listResult.status}
          tableId={ClpTableEnum.VehicleRegistrySettingsCustomFields}
        />
      </StyledTableContainer>
    </>
  )
}

export default CustomFieldGroups
